// ThemeToggle.tsx — three-way radio (light / system / dark).
// Plan ref: 06-00-PLAN §Task 1 step 7;
//           06-RESEARCH-theme-tokens.md §Code Examples §Theme Toggle Component.
//
// Used in Settings (06-05) and Cmd+K (06-06). The toggle is a radiogroup
// with role=radio buttons so screen readers announce it correctly.

import { Icon, Monitor, Moon, Sun } from '@/components/Icon';
import { cn } from '@/lib/cn';
import { useTheme } from './useTheme';
import type { ThemePref } from './preHydration';

interface Option {
  value: ThemePref;
  label: string;
  Icon: typeof Sun;
}

const OPTIONS: readonly Option[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'dark', label: 'Dark', Icon: Moon },
] as const;

export function ThemeToggle(): JSX.Element {
  const { theme, setTheme } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      data-testid="theme-toggle"
      className="inline-flex rounded-md bg-surface-2 p-0.5"
    >
      {OPTIONS.map(({ value, label, Icon: Glyph }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          aria-label={label}
          onClick={() => setTheme(value)}
          className={cn(
            'flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            theme === value
              ? 'bg-surface-0 text-fg-default shadow-card'
              : 'text-fg-muted hover:text-fg-default',
          )}
        >
          <Icon icon={Glyph} size={16} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Compact single-button variant (milestone-02 Phase 4, shell topbar) ──────
//
// The full 3-segment ThemeToggle (~200px) is too wide for the already-
// overflowing MainNav topbar (audit-2026-07 U2). This compact variant is a
// single icon button that CYCLES light → dark → system → light on click, so
// theme is discoverable from the shell header without the Cmd+K palette or the
// Settings tab (⚖U2 "no visible theme toggle").
//
// The glyph reflects the current *preference* (`theme`), NOT `resolvedTheme`:
// resolvedTheme is only ever light|dark, so it can never surface the System
// state — and a 3-way cycle whose System step is invisible is a trap. Showing
// Sun/Moon/Monitor for light/dark/system keeps all three states legible.
// (The Cmd+K "Switch theme to …" actions remain the explicit direct-set path.)

/** Cycle order + the glyph/label that represents each preference. */
const CYCLE: Record<ThemePref, { next: ThemePref; Glyph: typeof Sun; label: string }> = {
  light: { next: 'dark', Glyph: Sun, label: 'Light' },
  dark: { next: 'system', Glyph: Moon, label: 'Dark' },
  system: { next: 'light', Glyph: Monitor, label: 'System' },
};

export function ThemeToggleButton(): JSX.Element {
  const { theme, setTheme } = useTheme();
  const { next, Glyph, label } = CYCLE[theme];
  const nextLabel = CYCLE[next].label;
  return (
    <button
      type="button"
      data-testid="theme-toggle-button"
      aria-label={`Theme: ${label}. Switch to ${nextLabel}.`}
      title={`Theme: ${label} (click for ${nextLabel})`}
      onClick={() => setTheme(next)}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
        'text-fg-muted transition-colors hover:bg-overlay-hover hover:text-fg-default',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0',
      )}
    >
      <Icon icon={Glyph} size={16} />
    </button>
  );
}
